import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { supabase } from "./supabaseClient";
import { FiArrowLeft, FiStar, FiUser, FiClock, FiEdit2, FiTrash2 } from "react-icons/fi";
import { MdLocationOn, MdCategory, MdStar, MdStarBorder } from "react-icons/md";

function ListingDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [listing, setListing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [userReview, setUserReview] = useState(null);
  const [averageRating, setAverageRating] = useState(0);

  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [editingReviewId, setEditingReviewId] = useState(null);

  // Fetch user session and data
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [
          { data: listingData, error: listingError },
          { data: reviewData, error: reviewError }
        ] = await Promise.all([
          supabase.from("listings").select("*").eq("id", id).single(),
          supabase
            .from("reviews")
            .select(`
              *,
              profiles:user_id (
                id,
                email,
                full_name
              )
            `)
            .eq("listing_id", id)
            .order("created_at", { ascending: false })
        ]);

        if (!isMounted) return;

        if (listingError) throw listingError;
        if (reviewError) throw reviewError;

        setListing(listingData);
        const reviewsList = reviewData || [];
        setReviews(reviewsList);

        // Calculate average rating
        if (reviewsList.length > 0) {
          const avg = reviewsList.reduce((sum, review) => sum + review.rating, 0) / reviewsList.length;
          setAverageRating(avg);
        }

        // Check if user has already reviewed
        if (user) {
          const userReview = reviewsList.find(review => review.user_id === user.id);
          setUserReview(userReview);
          if (userReview) {
            setRating(userReview.rating);
            setComment(userReview.comment);
          }
        }

      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Unable to load listing details.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [id, user]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      alert("Please sign in to leave a review.");
      navigate("/login", { state: { from: `/listing/${id}` } });
      return;
    }

    setReviewLoading(true);

    try {
      if (editingReviewId) {
        // Update existing review
        const { error } = await supabase
          .from("reviews")
          .update({
            rating,
            comment,
            updated_at: new Date().toISOString()
          })
          .eq("id", editingReviewId);

        if (error) throw error;

        // Update local state
        setReviews(prev => prev.map(review => 
          review.id === editingReviewId 
            ? { ...review, rating, comment, updated_at: new Date().toISOString() }
            : review
        ));
        setEditingReviewId(null);
        
      } else {
        // Insert new review
        const { data, error } = await supabase
          .from("reviews")
          .insert({
            listing_id: id,
            user_id: user.id,
            rating,
            comment,
          })
          .select(`
            *,
            profiles:user_id (
              id,
              email,
              full_name
            )
          `)
          .single();

        if (error) throw error;

        // Update local state
        setReviews(prev => [data, ...prev]);
        setUserReview(data);
        
        // Recalculate average rating
        const newReviews = [data, ...reviews];
        const avg = newReviews.reduce((sum, review) => sum + review.rating, 0) / newReviews.length;
        setAverageRating(avg);
      }

      // Reset form if not editing
      if (!editingReviewId) {
        setComment("");
        setRating(5);
      }

    } catch (err) {
      console.error("Error submitting review:", err);
      alert("Error submitting review. Please try again.");
    } finally {
      setReviewLoading(false);
    }
  };

  const handleEditReview = (review) => {
    setRating(review.rating);
    setComment(review.comment);
    setEditingReviewId(review.id);
    // Scroll to form smoothly
    setTimeout(() => {
      document.getElementById('review-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleCancelEdit = () => {
    setRating(userReview?.rating || 5);
    setComment(userReview?.comment || "");
    setEditingReviewId(null);
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm("Are you sure you want to delete this review? This action cannot be undone.")) return;

    try {
      const { error } = await supabase
        .from("reviews")
        .delete()
        .eq("id", reviewId);

      if (error) throw error;

      // Update local state
      const updatedReviews = reviews.filter(review => review.id !== reviewId);
      setReviews(updatedReviews);
      setUserReview(null);
      
      // Reset form
      setComment("");
      setRating(5);
      setEditingReviewId(null);
      
      // Recalculate average rating
      if (updatedReviews.length > 0) {
        const avg = updatedReviews.reduce((sum, review) => sum + review.rating, 0) / updatedReviews.length;
        setAverageRating(avg);
      } else {
        setAverageRating(0);
      }

    } catch (err) {
      console.error("Error deleting review:", err);
      alert("Error deleting review. Please try again.");
    }
  };

  // Star rating component
  const StarRating = ({ rating, interactive = false, onRate }) => {
    return (
      <div style={{ display: 'flex', gap: '4px' }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            style={{
              fontSize: '24px',
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: interactive ? 'pointer' : 'default',
              color: star <= rating ? '#fbbf24' : '#d1d5db',
              transition: 'all 0.2s',
            }}
            onClick={() => interactive && onRate(star)}
            disabled={!interactive}
            onMouseEnter={(e) => {
              if (interactive) e.currentTarget.style.transform = 'scale(1.1)';
            }}
            onMouseLeave={(e) => {
              if (interactive) e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {star <= rating ? <MdStar /> : <MdStarBorder />}
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(to bottom right, #f9fafb, #f3f4f6)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            display: 'inline-block',
            width: '32px',
            height: '32px',
            border: '2px solid #000',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '16px'
          }}></div>
          <p style={{ color: '#374151' }}>Loading listing details...</p>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(to bottom right, #f9fafb, #f3f4f6)'
      }}>
        <div style={{
          textAlign: 'center',
          maxWidth: '448px',
          padding: '32px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ color: '#374151', marginBottom: '24px' }}>{error || "Listing not found."}</p>
          <Link 
            to="/" 
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: '#000',
              color: 'white',
              borderRadius: '8px',
              textDecoration: 'none',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#1f2937'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#000'}
          >
            <FiArrowLeft /> Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(to bottom right, #f9fafb, #f3f4f6)'
    }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          maxWidth: '896px',
          margin: '0 auto',
          padding: '16px'
        }}>
          <Link 
            to="/" 
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: '#374151',
              textDecoration: 'none',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#000'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#374151'}
          >
            <FiArrowLeft /> Back to Listings
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '896px',
        margin: '0 auto',
        padding: '32px 16px'
      }}>
        {/* Listing Header */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          padding: '32px',
          marginBottom: '32px'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: window.innerWidth < 1024 ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: window.innerWidth < 1024 ? 'start' : 'center',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div>
              <h1 style={{
                fontSize: '30px',
                fontWeight: 'bold',
                color: '#111827',
                marginBottom: '8px'
              }}>{listing.title}</h1>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                color: '#4b5563'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MdLocationOn style={{ color: '#9ca3af' }} />
                  <span>{listing.location || "Charlotte, NC"}</span>
                </div>
                {listing.category && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MdCategory style={{ color: '#9ca3af' }} />
                    <span>{listing.category}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Rating Summary */}
            {reviews.length > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                background: '#f9fafb',
                borderRadius: '12px',
                padding: '16px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{
                    fontSize: '36px',
                    fontWeight: 'bold',
                    color: '#111827'
                  }}>{averageRating.toFixed(1)}</div>
                  <StarRating rating={Math.round(averageRating)} />
                  <div style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    marginTop: '4px'
                  }}>
                    {reviews.length} review{reviews.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            )}
          </div>

          <p style={{
            fontSize: '18px',
            lineHeight: '1.75',
            color: '#374151'
          }}>{listing.description || "No description provided."}</p>
        </div>

        {/* Reviews Section */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          padding: '32px',
          marginBottom: '32px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
          }}>
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#111827'
            }}>
              Reviews ({reviews.length})
            </h2>
            {userReview && (
              <div style={{
                fontSize: '14px',
                color: '#4b5563',
                background: '#f9fafb',
                padding: '8px 16px',
                borderRadius: '8px'
              }}>
                You reviewed this listing
              </div>
            )}
          </div>

          {reviews.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '48px',
              border: '2px dashed #e5e7eb',
              borderRadius: '12px'
            }}>
              <FiStar style={{
                fontSize: '36px',
                color: '#d1d5db',
                margin: '0 auto 16px'
              }} />
              <p style={{ color: '#6b7280' }}>No reviews yet. Be the first to review!</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {reviews.map((review) => (
                <div 
                  key={review.id} 
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    padding: '24px',
                    transition: 'all 0.2s',
                    background: review.user_id === user?.id ? '#eff6ff' : 'white',
                    borderColor: review.user_id === user?.id ? '#bfdbfe' : '#e5e7eb'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    marginBottom: '16px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        background: 'linear-gradient(to bottom right, #e5e7eb, #d1d5db)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}>
                        <FiUser style={{ color: '#4b5563' }} />
                      </div>
                      <div>
                        <div style={{
                          fontWeight: '500',
                          color: '#111827'
                        }}>
                          {review.profiles?.full_name || review.profiles?.email || "Anonymous"}
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          color: '#6b7280'
                        }}>
                          <FiClock style={{ width: '12px', height: '12px' }} />
                          {new Date(review.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                          {review.updated_at !== review.created_at && (
                            <span style={{ fontSize: '12px', color: '#9ca3af' }}>(edited)</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <StarRating rating={review.rating} />
                      {review.user_id === user?.id && (
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleEditReview(review)}
                            style={{
                              padding: '8px',
                              color: '#4b5563',
                              background: 'none',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#2563eb';
                              e.currentTarget.style.background = '#dbeafe';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#4b5563';
                              e.currentTarget.style.background = 'none';
                            }}
                            title="Edit review"
                          >
                            <FiEdit2 style={{ width: '16px', height: '16px' }} />
                          </button>
                          <button
                            onClick={() => handleDeleteReview(review.id)}
                            style={{
                              padding: '8px',
                              color: '#4b5563',
                              background: 'none',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = '#dc2626';
                              e.currentTarget.style.background = '#fee2e2';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = '#4b5563';
                              e.currentTarget.style.background = 'none';
                            }}
                            title="Delete review"
                          >
                            <FiTrash2 style={{ width: '16px', height: '16px' }} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <p style={{
                    color: '#374151',
                    lineHeight: '1.6'
                  }}>{review.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Review Form */}
        <div id="review-form" style={{
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          padding: '32px',
          scrollMarginTop: '32px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: '24px'
          }}>
            {editingReviewId ? "Edit Your Review" : userReview ? "Update Your Review" : "Leave a Review"}
          </h2>
          
          {!user ? (
            <div style={{
              textAlign: 'center',
              padding: '32px',
              border: '2px dashed #e5e7eb',
              borderRadius: '12px'
            }}>
              <p style={{ color: '#4b5563', marginBottom: '16px' }}>Please sign in to leave a review</p>
              <button
                onClick={() => navigate("/login", { state: { from: `/listing/${id}` } })}
                style={{
                  padding: '12px 24px',
                  background: '#000',
                  color: 'white',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#1f2937'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#000'}
              >
                Sign In
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmitReview} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <label style={{
                  display: 'block',
                  color: '#374151',
                  fontWeight: '500',
                  marginBottom: '12px'
                }}>
                  Your Rating
                </label>
                <StarRating 
                  rating={rating} 
                  interactive={true}
                  onRate={setRating}
                />
                <div style={{
                  marginTop: '8px',
                  fontSize: '14px',
                  color: '#6b7280'
                }}>
                  Selected: {rating} star{rating !== 1 ? 's' : ''}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  color: '#374151',
                  fontWeight: '500',
                  marginBottom: '8px'
                }}>
                  Your Review
                </label>
                <textarea
                  placeholder="Share your experience with this listing..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    height: '160px',
                    padding: '16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '12px',
                    resize: 'none',
                    outline: 'none',
                    transition: 'all 0.2s',
                    fontFamily: 'inherit',
                    fontSize: '14px'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#000';
                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0,0,0,0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '16px' }}>
                <button
                  type="submit"
                  disabled={reviewLoading}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    background: '#000',
                    color: 'white',
                    borderRadius: '12px',
                    border: 'none',
                    cursor: reviewLoading ? 'not-allowed' : 'pointer',
                    opacity: reviewLoading ? 0.5 : 1,
                    transition: 'all 0.2s',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => {
                    if (!reviewLoading) e.currentTarget.style.background = '#1f2937';
                  }}
                  onMouseLeave={(e) => {
                    if (!reviewLoading) e.currentTarget.style.background = '#000';
                  }}
                >
                  {reviewLoading ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid white',
                        borderTopColor: 'transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      {editingReviewId ? "Updating..." : "Posting..."}
                    </span>
                  ) : (
                    editingReviewId ? "Update Review" : userReview ? "Update Review" : "Submit Review"
                  )}
                </button>
                
                {editingReviewId && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    style={{
                      padding: '12px 24px',
                      border: '1px solid #d1d5db',
                      color: '#374151',
                      borderRadius: '12px',
                      background: 'white',
                      cursor: 'pointer',
                      transition: 'background 0.2s',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    Cancel
                  </button>
                )}
              </div>
              
              {userReview && !editingReviewId && (
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  textAlign: 'center'
                }}>
                  You can edit or delete your review at any time
                </p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ListingDetails;